import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import {
  EMAIL_TEMPLATE_BY_KEY,
  EMAIL_TEMPLATE_CATALOG,
  missingRequiredPlaceholders,
  unknownPlaceholderMessage,
  unknownPlaceholders,
} from "./email-templates/catalog";
import { renderEmailTemplate } from "./email-templates/render";
import { resolveEmailSender } from "./email-sender";
import { enqueueEmail } from "@/lib/email-enqueue.server";
import { loadSampleSources, resolveSampleValues } from "./email-templates/sample-source.server";

/** Ticket TM1: Herkunft der Testwerte – echter Vorgang vor Beispielwert. */
const SAMPLE_ORIGIN = "https://creategy.de";

async function sampleValuesFor(ctx: Ctx, key: string, recipient: string) {
  const sources = await loadSampleSources(ctx.supabase, {
    recipient,
    origin: SAMPLE_ORIGIN,
    userId: ctx.userId,
  });
  return resolveSampleValues(key, sources, recipient);
}

async function actingEmail(ctx: Ctx): Promise<string> {
  const { data, error } = await ctx.supabase
    .from("profiles")
    .select("email")
    .eq("id", ctx.userId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  const email = data?.email as string | undefined;
  if (!email) throw new Error("Für Ihr Konto ist keine E-Mail-Adresse hinterlegt.");
  return email;
}

type Ctx = { supabase: any; userId: string };

async function assertStaff(ctx: Ctx) {
  const [{ data: isAdmin }, { data: isEditor }] = await Promise.all([
    ctx.supabase.rpc("has_role", { _user_id: ctx.userId, _role: "admin" }),
    ctx.supabase.rpc("has_role", { _user_id: ctx.userId, _role: "editor" }),
  ]);
  if (!isAdmin && !isEditor) throw new Error("Forbidden: Interner Zugang erforderlich");
}

export type EmailTemplateListItem = {
  key: string;
  displayName: string;
  purpose: string;
  security: boolean;
  subject: string;
  customized: boolean;
  updatedAt: string | null;
  updatedByName: string | null;
};

export const listEmailTemplates = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<EmailTemplateListItem[]> => {
    await assertStaff(context);
    const { data, error } = await context.supabase
      .from("email_templates")
      .select("key, subject, updated_at, updated_by");
    if (error) throw new Error(error.message);

    const rows = (data ?? []) as any[];
    const editorIds = [...new Set(rows.map((r) => r.updated_by).filter(Boolean))];
    let names: Record<string, string> = {};
    if (editorIds.length) {
      // Nur der Anzeigename der bearbeitenden Person. Läuft über den
      // privilegierten Serverzugang, damit Redaktionskonten keinen
      // Vollzugriff auf die Personalprofile brauchen.
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { data: profiles } = await supabaseAdmin
        .from("profiles")
        .select("id, full_name")
        .in("id", editorIds);
      names = Object.fromEntries(((profiles ?? []) as any[]).map((p) => [p.id, p.full_name || ""]));
    }

    const byKey = new Map(rows.map((r) => [r.key, r]));
    return EMAIL_TEMPLATE_CATALOG.map((def) => {
      const row = byKey.get(def.key);
      return {
        key: def.key,
        displayName: def.displayName,
        purpose: def.purpose,
        security: Boolean(def.security),
        subject: row?.subject ?? def.defaultSubject,
        customized: Boolean(row),
        updatedAt: row?.updated_at ?? null,
        updatedByName: row?.updated_by ? (names[row.updated_by] ?? null) : null,
      };
    });
  });

export type EmailTemplateDetail = {
  key: string;
  displayName: string;
  purpose: string;
  security: boolean;
  subject: string;
  bodyMd: string;
  defaultSubject: string;
  defaultBodyMd: string;
  customized: boolean;
  updatedAt: string | null;
  placeholders: { name: string; description: string; required: boolean; sample: string }[];
};

export const getEmailTemplate = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { key: string }) => z.object({ key: z.string().min(1) }).parse(d))
  .handler(async ({ data, context }): Promise<EmailTemplateDetail> => {
    await assertStaff(context);
    const def = EMAIL_TEMPLATE_BY_KEY[data.key];
    if (!def) throw new Error("Unbekannte Vorlage");

    const { data: row, error } = await context.supabase
      .from("email_templates")
      .select("subject, body_md, updated_at")
      .eq("key", data.key)
      .maybeSingle();
    if (error) throw new Error(error.message);

    return {
      key: def.key,
      displayName: def.displayName,
      purpose: def.purpose,
      security: Boolean(def.security),
      subject: row?.subject ?? def.defaultSubject,
      bodyMd: row?.body_md ?? def.defaultBodyMd,
      defaultSubject: def.defaultSubject,
      defaultBodyMd: def.defaultBodyMd,
      customized: Boolean(row),
      updatedAt: row?.updated_at ?? null,
      placeholders: def.placeholders.map((p) => ({
        name: p.name,
        description: p.description,
        required: Boolean(p.required),
        sample: p.sample,
      })),
    };
  });

export const saveEmailTemplate = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { key: string; subject: string; bodyMd: string }) =>
    z
      .object({
        key: z.string().min(1),
        subject: z.string().trim().min(3).max(200),
        bodyMd: z.string().trim().min(10).max(20000),
      })
      .parse(d),
  )
  .handler(async ({ data, context }) => {
    await assertStaff(context);
    const def = EMAIL_TEMPLATE_BY_KEY[data.key];
    if (!def) throw new Error("Unbekannte Vorlage");

    const missing = missingRequiredPlaceholders(data.key, data.subject, data.bodyMd);
    if (missing.length) {
      throw new Error(`Pflicht-Platzhalter fehlen: ${missing.map((m) => `{{${m}}}`).join(", ")}`);
    }

    // Ticket Z15a: Ein unbekannter Platzhalter verhindert das Speichern, damit er
    // nicht unaufgelöst in einer Kundenmail landet.
    const unknown = unknownPlaceholders(data.key, data.subject, data.bodyMd);
    if (unknown.length) {
      throw new Error(unknownPlaceholderMessage(data.key, unknown));
    }

    const { error } = await context.supabase.from("email_templates").upsert(
      {
        key: data.key,
        subject: data.subject,
        body_md: data.bodyMd,
        updated_by: context.userId,
      },
      { onConflict: "key" },
    );
    if (error) throw new Error(error.message);

    return { ok: true };
  });

export const resetEmailTemplate = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { key: string }) => z.object({ key: z.string().min(1) }).parse(d))
  .handler(async ({ data, context }) => {
    await assertStaff(context);
    const def = EMAIL_TEMPLATE_BY_KEY[data.key];
    if (!def) throw new Error("Unbekannte Vorlage");

    const { error } = await context.supabase.from("email_templates").delete().eq("key", data.key);
    if (error) throw new Error(error.message);

    return { ok: true, subject: def.defaultSubject, bodyMd: def.defaultBodyMd };
  });

/** Rendert die (ungespeicherte) Fassung mit Beispielwerten für die Vorschau. */
export const previewEmailTemplate = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { key: string; subject: string; bodyMd: string }) =>
    z
      .object({ key: z.string().min(1), subject: z.string(), bodyMd: z.string().max(20000) })
      .parse(d),
  )
  .handler(async ({ data, context }) => {
    await assertStaff(context);
    if (!EMAIL_TEMPLATE_BY_KEY[data.key]) throw new Error("Unbekannte Vorlage");
    // Ticket TM1: Vorschau zeigt die Werte des jüngsten echten Vorgangs.
    const recipientForPreview = await actingEmail(context);
    const sample = await sampleValuesFor(context, data.key, recipientForPreview);
    const rendered = renderEmailTemplate({
      subject: data.subject,
      bodyMd: data.bodyMd,
      values: sample.values,
    });
    return { ...rendered, sourceNote: sample.note, sampleFallbacks: sample.merged.beispiel };
  });

export const sendEmailTemplateTest = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { key: string; subject: string; bodyMd: string }) =>
    z
      .object({
        key: z.string().min(1),
        subject: z.string().trim().min(3).max(200),
        bodyMd: z.string().trim().min(10).max(20000),
      })
      .parse(d),
  )
  .handler(async ({ data, context }) => {
    // Ticket AD3a: Testversand ist wie der Sammelversand Administratoren vorbehalten.
    const { data: isAdminForTest } = await context.supabase.rpc("has_role", {
      _user_id: context.userId,
      _role: "admin",
    });
    if (!isAdminForTest) throw new Error("Forbidden: Admin role required");

    const def = EMAIL_TEMPLATE_BY_KEY[data.key];
    if (!def) throw new Error("Unbekannte Vorlage");

    // Ticket Z15a: gleiche Prüfung wie beim Speichern, damit keine Testmail mit
    // unbekanntem Platzhalter hinausgeht.
    const unknownForTest = unknownPlaceholders(data.key, data.subject, data.bodyMd);
    if (unknownForTest.length) {
      throw new Error(unknownPlaceholderMessage(data.key, unknownForTest));
    }

    const recipient = await actingEmail(context);

    const sample = await sampleValuesFor(context, data.key, recipient);
    const rendered = renderEmailTemplate({
      subject: `[Test] ${data.subject}`,
      bodyMd: data.bodyMd,
      values: sample.values,
    });

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const messageId = crypto.randomUUID();

    await supabaseAdmin.from("email_send_log").insert({
      message_id: messageId,
      template_name: `${data.key}-test`,
      recipient_email: recipient,
      status: "pending",
    });

    const { getUnsubscribeToken } = await import("./email-unsubscribe.server");
    const unsubscribeToken = await getUnsubscribeToken(supabaseAdmin, recipient);

    const { error } = await enqueueEmail(supabaseAdmin, {
      queue_name: "transactional_emails",
      payload: {
        message_id: messageId,
        to: recipient,
        from: resolveEmailSender(data.key).from,
        reply_to: resolveEmailSender(data.key).replyTo,
        sender_domain: resolveEmailSender(data.key).senderDomain,
        subject: rendered.subject,
        html: rendered.html,
        text: rendered.text,
        purpose: resolveEmailSender(data.key).purpose,
        label: `${data.key}-test`,
        idempotency_key: `tpl-test-${data.key}-${messageId}`,
        unsubscribe_token: unsubscribeToken,

        queued_at: new Date().toISOString(),
      },
    });
    if (error) throw new Error(error.message);

    await (await import("@/lib/audit.server")).writeAuditEvent({
      action: "EMAIL_TEMPLATE_TEST_SEND",
      tableName: "email_templates",
      recordId: data.key,
      newData: { key: data.key, recipient },
      context: "admin/emails",
      actorId: context.userId,
    });

    return { ok: true, recipient, sourceNote: sample.note };
  });

export type BulkTestResult = { key: string; displayName: string; ok: boolean; error?: string };

/**
 * Sammel-Testversand: schickt jede Katalogvorlage einmal mit Beispielwerten an
 * eine interne Adresse. Nur für Administratoren, Empfänger stets @creategy.de.
 */
export const sendAllEmailTemplateTests = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { recipient: string }) =>
    z
      .object({
        recipient: z
          .string()
          .trim()
          .toLowerCase()
          .email()
          .refine((v) => v.endsWith("@creategy.de"), {
            message: "Testmails sind nur an creategy.de-Adressen möglich.",
          }),
      })
      .parse(d),
  )
  .handler(async ({ data, context }) => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", {
      _user_id: context.userId,
      _role: "admin",
    });
    if (!isAdmin) throw new Error("Forbidden: Administratorrechte erforderlich");

    const recipient = data.recipient;

    const { data: rows, error: rowsErr } = await context.supabase
      .from("email_templates")
      .select("key, subject, body_md");
    if (rowsErr) throw new Error(rowsErr.message);
    const stored = new Map(
      ((rows ?? []) as any[]).map((r) => [r.key, { subject: r.subject, bodyMd: r.body_md }]),
    );

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { getUnsubscribeToken } = await import("./email-unsubscribe.server");
    const unsubscribeToken = await getUnsubscribeToken(supabaseAdmin, recipient);

    // Ticket TM1: einmal je Lauf laden, dann für jede Vorlage wiederverwenden.
    const sources = await loadSampleSources(context.supabase, {
      recipient,
      origin: SAMPLE_ORIGIN,
      userId: context.userId,
    });

    const results: BulkTestResult[] = [];

    for (const def of EMAIL_TEMPLATE_CATALOG) {
      const source = stored.get(def.key);
      const subject = source?.subject ?? def.defaultSubject;
      const bodyMd = source?.bodyMd ?? def.defaultBodyMd;
      try {
        const rendered = renderEmailTemplate({
          subject: `[Test] ${subject}`,
          bodyMd,
          values: resolveSampleValues(def.key, sources, recipient).values,
        });
        const messageId = crypto.randomUUID();

        await supabaseAdmin.from("email_send_log").insert({
          message_id: messageId,
          template_name: `${def.key}-test`,
          recipient_email: recipient,
          status: "pending",
        });

        const { error } = await enqueueEmail(supabaseAdmin, {
          queue_name: "transactional_emails",
          payload: {
            message_id: messageId,
            to: recipient,
            from: resolveEmailSender(def.key).from,
            reply_to: resolveEmailSender(def.key).replyTo,
            sender_domain: resolveEmailSender(def.key).senderDomain,
            subject: rendered.subject,
            html: rendered.html,
            text: rendered.text,
            purpose: resolveEmailSender(def.key).purpose,
            label: `${def.key}-test`,
            idempotency_key: `tpl-test-${def.key}-${messageId}`,
            unsubscribe_token: unsubscribeToken,
            queued_at: new Date().toISOString(),
          },
        });
        if (error) throw new Error(error.message);

        results.push({ key: def.key, displayName: def.displayName, ok: true });
      } catch (err) {
        results.push({
          key: def.key,
          displayName: def.displayName,
          ok: false,
          error: err instanceof Error ? err.message : "Unbekannter Fehler",
        });
      }
    }

    await (await import("@/lib/audit.server")).writeAuditEvent({
      action: "EMAIL_TEMPLATE_TEST_SEND_ALL",
      tableName: "email_templates",
      recordId: recipient,
      newData: {
        recipient,
        total: results.length,
        failed: results.filter((r) => !r.ok).map((r) => r.key),
      },
      context: "admin/emails",
      actorId: context.userId,
    });

    return { recipient, results };
  });
